import React, { useEffect, useMemo, useState } from "react";
import { Users, Building2, ChevronDown, CheckCircle, AlertTriangle, Search, X } from "lucide-react";
import ReusableDropdown from "../../../../../components/ReusableDropdown";

const StepChooseEmployees = ({
  payrollData = [],
  rowsLoading = false,
  selectedEmployees = [],
  setSelectedEmployees,
  handleEmployeeSelection,
  setSelectedAll,
  selectedCompanyIds = [], // from formData.companyIds (Step 1) — [] means no company filter applied
  modalStyles,
  currentTheme,
  t,
  language = "en",
  theme = "light",
}) => {
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const isAr = language === "ar";

  // Resolve a display/grouping department name for an employee row.
  // Rows normally carry `department` as a string (mapped from the backend's
  // department relation), but stay defensive: the raw backend shape has
  // department as {id, name}, and legacy rows only had employeeType
  // (since renamed to position).
  const resolveDepartment = (employee) =>
    employee.department?.name ??
    (typeof employee.department === "string" && employee.department
      ? employee.department
      : null) ??
    employee.position ??
    employee.employeeType ??
    "Unknown";
  const L = (ar, en) => (isAr ? ar : en);

  // ── Search / department filter ─────────────────────────────────────────
  // VIEW-ONLY: narrows which rows are shown (and what "select all"/group
  // checkboxes act on). It never changes who is already selected — processing
  // always uses `selectedEmployees`, so hidden-but-selected employees are
  // still processed and still counted in the totals below.
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");

  const toggleGroup = (groupId, e) => {
    e.stopPropagation();
    setCollapsedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // ── Paid employees are filtered out at the data level — not just hidden
  // with CSS — so they can never appear in the grouped lists, be picked up
  // by "select all", or be counted in the selected totals below. ──
  const unpaidPayrollData = useMemo(
    () => payrollData.filter((employee) => employee.status !== "paid"),
    [payrollData],
  );
  const paidCount = payrollData.length - unpaidPayrollData.length;

  // Defensive cleanup: if an employee inside `selectedEmployees` becomes
  // "paid" after the fact (e.g. payrollData refreshes mid-flow), drop them
  // from the selection instead of letting a paid employee linger in state.
  useEffect(() => {
    const paidIds = new Set(
      payrollData.filter((emp) => emp.status === "paid").map((emp) => emp.id),
    );
    if (paidIds.size === 0) return;

    const hasStalePaidSelection = selectedEmployees.some((emp) =>
      paidIds.has(emp.id),
    );
    if (hasStalePaidSelection) {
      setSelectedEmployees((prev) =>
        prev.filter((emp) => !paidIds.has(emp.id)),
      );
    }
  }, [payrollData, selectedEmployees, setSelectedEmployees]);

  // The only selections that should ever drive UI / totals are ones that
  // still exist in the unpaid list. This protects the summary at the
  // bottom even if `selectedEmployees` ever contains a stale paid entry.
  // NOTE: this is intentionally NOT restricted to the selected companies —
  // it tracks the full cross-company selection so totals stay correct even
  // while the visible list below is scoped to the active company filter.
  const validSelectedEmployees = useMemo(
    () =>
      selectedEmployees.filter((emp) =>
        unpaidPayrollData.some((u) => u.id === emp.id),
      ),
    [selectedEmployees, unpaidPayrollData],
  );

  // ── Company scoping ─────────────────────────────────────────────────────
  // If one or more companies were selected in Step 1, only show employees
  // who belong to one of those companies. With zero companies selected
  // (legacy single-company flow, or companies haven't loaded), show everyone.
  const hasCompanyFilter = selectedCompanyIds.length > 0;
  const isMultiCompany = selectedCompanyIds.length > 1;

  const scopedPayrollData = useMemo(() => {
    if (!hasCompanyFilter) return unpaidPayrollData;
    return unpaidPayrollData.filter((emp) =>
      selectedCompanyIds.includes(emp.workingCompany),
    );
  }, [unpaidPayrollData, selectedCompanyIds, hasCompanyFilter]);

  // Arabic-friendly matching: ignore case, diacritics and the common
  // alef / ta-marbuta / ya spelling variants (أحمد = احمد, فاطمة = فاطمه).
  const normalizeText = (value) =>
    String(value ?? "")
      .toLowerCase()
      .replace(/[\u064B-\u0652\u0640]/g, "")
      .replace(/[أإآ]/g, "ا")
      .replace(/ة/g, "ه")
      .replace(/ى/g, "ي")
      .replace(/\s+/g, " ")
      .trim();

  const departmentOptions = useMemo(
    () =>
      [...new Set(scopedPayrollData.map((emp) => resolveDepartment(emp)))].sort((a, b) =>
        String(a).localeCompare(String(b), isAr ? "ar" : "en"),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopedPayrollData, isAr],
  );

  // A department that disappeared (e.g. company filter changed) resets the filter.
  useEffect(() => {
    if (departmentFilter && !departmentOptions.includes(departmentFilter)) setDepartmentFilter("");
  }, [departmentOptions, departmentFilter]);

  const normalizedQuery = normalizeText(searchQuery);
  const isFiltering = normalizedQuery.length > 0 || departmentFilter !== "";

  const visiblePayrollData = useMemo(() => {
    if (!isFiltering) return scopedPayrollData;
    return scopedPayrollData.filter((emp) => {
      const dept = resolveDepartment(emp);
      if (departmentFilter && dept !== departmentFilter) return false;
      if (!normalizedQuery) return true;
      const haystack = normalizeText(
        [emp.employeeName, dept, emp.infinityCode, emp.clientCode, emp.workingCompany].filter(Boolean).join(" "),
      );
      // Every word typed must appear somewhere (name OR department OR code).
      return normalizedQuery.split(" ").every((word) => haystack.includes(word));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedPayrollData, normalizedQuery, departmentFilter, isFiltering]);

  const clearFilters = () => {
    setSearchQuery("");
    setDepartmentFilter("");
  };

  // Groups are always shown expanded while filtering, so a match inside a
  // collapsed department is never hidden.
  const isCollapsed = (key) => !isFiltering && collapsedGroups[key];

  const hiddenByCompanyFilterCount = hasCompanyFilter
    ? unpaidPayrollData.length - scopedPayrollData.length
    : 0;

  // ── Grouping ─────────────────────────────────────────────────────────────
  // Single/no company selected → flat department grouping (original behavior).
  // Multiple companies selected → company groups, each with nested department
  // sub-groups, so employees never get mixed across companies visually.

  const groupedByDepartment = useMemo(
    () =>
      visiblePayrollData.reduce((acc, employee) => {
        const dept = resolveDepartment(employee);
        if (!acc[dept]) acc[dept] = [];
        acc[dept].push(employee);
        return acc;
      }, {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visiblePayrollData],
  );

  const groupedByCompanyThenDepartment = useMemo(() => {
    if (!isMultiCompany) return null;
    return visiblePayrollData.reduce((acc, employee) => {
      const company = employee.workingCompany || "Unknown";
      const dept = resolveDepartment(employee);
      if (!acc[company]) acc[company] = {};
      if (!acc[company][dept]) acc[company][dept] = [];
      acc[company][dept].push(employee);
      return acc;
    }, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePayrollData, isMultiCompany]);

  // ── Generic group selection helpers (work for both department-only and
  // company/department-nested groups, since both just operate on an
  // employee[] for a given group). ──
  const isGroupFullySelected = (employees) =>
    employees.every((emp) =>
      validSelectedEmployees.some((selected) => selected.id === emp.id),
    );

  const isGroupPartiallySelected = (employees) =>
    employees.some((emp) =>
      validSelectedEmployees.some((selected) => selected.id === emp.id),
    ) && !isGroupFullySelected(employees);

  const handleGroupSelection = (employees) => {
    const allSelected = isGroupFullySelected(employees);

    if (allSelected) {
      setSelectedEmployees((prev) =>
        prev.filter(
          (selected) => !employees.some((emp) => emp.id === selected.id),
        ),
      );
    } else {
      setSelectedEmployees((prev) => {
        const newEmployees = employees.filter(
          (emp) => !prev.some((selected) => selected.id === emp.id),
        );
        return [...prev, ...newEmployees];
      });
    }
  };

  // "Select all" reflects the currently SCOPED (company-filtered) list, so
  // toggling it never silently grabs or drops employees from a company that
  // isn't even visible right now.
  const allScopedSelected =
    scopedPayrollData.length > 0 &&
    scopedPayrollData.every((emp) =>
      validSelectedEmployees.some((selected) => selected.id === emp.id),
    );

  // Keep the parent's `selectedAll` flag in sync with the scoped view.
  useEffect(() => {
    setSelectedAll(allScopedSelected);
  }, [allScopedSelected, setSelectedAll]);

  // The header checkbox acts on what is VISIBLE: all scoped rows normally,
  // only the matching rows while a search/department filter is active.
  const allVisibleSelected =
    visiblePayrollData.length > 0 &&
    visiblePayrollData.every((emp) => validSelectedEmployees.some((s) => s.id === emp.id));
  const someVisibleSelected =
    visiblePayrollData.some((emp) => validSelectedEmployees.some((s) => s.id === emp.id)) &&
    !allVisibleSelected;

  const handleSelectAllToggle = () => {
    if (visiblePayrollData.length === 0) return;
    handleGroupSelection(visiblePayrollData);
  };

  // Selected employees the current search/filter is hiding — still selected
  // and still processed; surfaced so nobody is surprised by the total.
  const hiddenSelectedCount = isFiltering
    ? validSelectedEmployees.filter((emp) => !visiblePayrollData.some((v) => v.id === emp.id)).length
    : 0;

  // Per-company breakdown for the summary footer when multiple companies
  // are active — makes it obvious how the total splits across companies
  // before processing runs each company's payroll separately.
  const selectedByCompany = useMemo(() => {
    if (!isMultiCompany) return null;
    const map = {};
    validSelectedEmployees.forEach((emp) => {
      const company = emp.workingCompany || "Unknown";
      if (!map[company]) map[company] = { count: 0, total: 0 };
      map[company].count += 1;
      map[company].total += emp.netSalary;
    });
    return map;
  }, [validSelectedEmployees, isMultiCompany]);

  // ── Shared row renderers ────────────────────────────────────────────────
  const c = currentTheme.colors;
  const money = (value) =>
    `${Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${t("currency")}`;
  const initials = (name) =>
    String(name || "?")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  const checkboxStyle = { width: "18px", height: "18px", margin: 0, cursor: "pointer", flexShrink: 0 };

  const renderEmployeeRow = (employee) => {
    const isSelected = validSelectedEmployees.some(
      (emp) => emp.id === employee.id,
    );
    const code = employee.infinityCode || employee.clientCode;

    return (
      <div
        key={employee.id}
        className="pp-row"
        data-selected={isSelected}
        style={{
          ...modalStyles.employeeItem,
          padding: "12px 20px",
          paddingInlineStart: "20px",
          borderTop: `1px solid ${c.borderLight}`,
          borderInlineStart: `3px solid ${isSelected ? c.primary : "transparent"}`,
          background: isSelected ? c.primarySoft : c.surface,
          transition: "background 0.15s",
        }}
        onClick={() => handleEmployeeSelection(employee)}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => {}}
          style={checkboxStyle}
        />
        <div
          aria-hidden="true"
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: isSelected ? c.primary : c.surfaceSubtle,
            color: isSelected ? "#ffffff" : c.textSecondary,
            border: `1px solid ${isSelected ? c.primary : c.borderLight}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "13px",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initials(employee.employeeName)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              color: c.textPrimary,
              fontSize: "15px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {employee.employeeName}
          </div>
          {code && (
            <div style={{ fontSize: "12.5px", color: c.textMuted, marginTop: "2px" }}>
              #{code}
            </div>
          )}
        </div>
        <div style={{ textAlign: "end", flexShrink: 0 }}>
          <div
            style={{
              fontWeight: 700,
              color: c.textPrimary,
              fontSize: "15px",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {money(employee.netSalary)}
          </div>
          <div style={{ fontSize: "12px", color: c.textMuted, marginTop: "2px" }}>
            {L("صافي الراتب", "Net salary")}
          </div>
        </div>
      </div>
    );
  };

  const renderGroupHeader = ({
    title,
    icon,
    employees,
    total,
    collapseKey,
    sticky,
    level = "department",
  }) => {
    const isFullySelected = isGroupFullySelected(employees);
    const isPartiallySelected = isGroupPartiallySelected(employees);
    const selectedInGroup = employees.filter((emp) =>
      validSelectedEmployees.some((s) => s.id === emp.id),
    ).length;

    return (
      <div
        className="pp-group-head"
        style={{
          padding: level === "company" ? "16px 20px" : "13px 20px",
          background: level === "company" ? c.primarySoft : c.surfaceSubtle,
          borderTop: `1px solid ${c.borderLight}`,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "14px",
          transition: "background 0.15s",
          ...(sticky && { position: "sticky", top: 0, zIndex: 2 }),
        }}
        onClick={() => handleGroupSelection(employees)}
      >
        <input
          type="checkbox"
          checked={isFullySelected}
          onChange={() => {}}
          ref={(el) => {
            if (el) el.indeterminate = isPartiallySelected;
          }}
          style={checkboxStyle}
        />
        {icon}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: level === "company" ? "16px" : "15px",
              color: c.textPrimary,
            }}
          >
            {title}
          </div>
          <div style={{ fontSize: "12.5px", color: c.textMuted, marginTop: "2px" }}>
            {L(`${employees.length} موظف`, `${employees.length} employee${employees.length === 1 ? "" : "s"}`)}
            {selectedInGroup > 0 && (
              <span style={{ color: c.primary, fontWeight: 600 }}>
                {" · "}
                {L(`${selectedInGroup} محدد`, `${selectedInGroup} selected`)}
              </span>
            )}
          </div>
        </div>
        <div style={{ textAlign: "end", flexShrink: 0 }}>
          <div
            style={{
              fontSize: "14.5px",
              fontWeight: 700,
              color: c.textPrimary,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {money(total)}
          </div>
          <div style={{ fontSize: "12px", color: c.textMuted, marginTop: "2px" }}>
            {L("الإجمالي", "Total")}
          </div>
        </div>
        <button
          type="button"
          aria-label={isCollapsed(collapseKey) ? L("توسيع", "Expand") : L("طي", "Collapse")}
          onClick={(e) => toggleGroup(collapseKey, e)}
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "8px",
            border: "none",
            background: "transparent",
            color: c.textMuted,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <ChevronDown
            size={18}
            style={{
              transform: isCollapsed(collapseKey) ? `rotate(${isAr ? 90 : -90}deg)` : "none",
              transition: "transform 0.2s",
            }}
          />
        </button>
      </div>
    );
  };

  const renderDepartmentGroup = (department, employees, { nested = false } = {}) => {
    const collapseKey = `dept-${department}`;
    return (
      <div key={department}>
        {renderGroupHeader({
          title: department,
          employees,
          total: employees.reduce((sum, emp) => sum + emp.netSalary, 0),
          collapseKey,
          sticky: !nested,
        })}
        {!isCollapsed(collapseKey) && employees.map(renderEmployeeRow)}
      </div>
    );
  };

  const renderCompanyGroup = (company, departmentsMap) => {
    const companyEmployees = Object.values(departmentsMap).flat();
    const collapseKey = `comp-${company}`;
    return (
      <div key={company}>
        {renderGroupHeader({
          title: company,
          icon: <Building2 size={18} style={{ color: c.primary, flexShrink: 0 }} />,
          employees: companyEmployees,
          total: companyEmployees.reduce((sum, emp) => sum + emp.netSalary, 0),
          collapseKey,
          sticky: true,
          level: "company",
        })}
        {!isCollapsed(collapseKey) &&
          Object.entries(departmentsMap).map(([department, employees]) =>
            renderDepartmentGroup(department, employees, { nested: true }),
          )}
      </div>
    );
  };

  const notice = (tone, icon, content) => (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        padding: "12px 16px",
        borderRadius: "10px",
        background: tone === "warning" ? c.warningSoft : c.infoSoft,
        border: `1px solid ${tone === "warning" ? `${c.warning}33` : `${c.info}33`}`,
        color: c.textSecondary,
        fontSize: "13.5px",
        lineHeight: 1.5,
      }}
    >
      {icon}
      <span>{content}</span>
    </div>
  );

  const controlStyle = {
    height: "46px",
    boxSizing: "border-box",
    borderRadius: "10px",
    border: `1px solid ${c.border}`,
    background: c.inputBackground,
    color: c.textPrimary,
    fontSize: "14.5px",
    outline: "none",
    transition: "border-color 0.15s, box-shadow 0.15s",
  };

  return (
    <div className="pp-section" style={{ ...modalStyles.formSection, marginBottom: 0 }}>
      {/* ── Title ─────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: "22px" }}>
        <h3 style={modalStyles.sectionTitle}>
          <Users size={20} style={{ color: c.primary }} />
          {isMultiCompany ? t("selectByCompany") : t("selectByDepartment")}
        </h3>
        <p style={modalStyles.sectionSubtitle}>
          {L(
            "اختر الموظفين المطلوب احتساب رواتبهم في هذه الدورة. يمكنك التحديد بالقسم كاملاً أو موظف بموظف.",
            "Choose who is included in this payroll run — select a whole department at once or pick employees one by one.",
          )}
        </p>
      </div>

      {/* ── Toolbar: search + department filter + select all ─────────── */}
      <div className="pp-toolbar" style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "14px" }}>
        {scopedPayrollData.length > 0 && (
          <>
            <div style={{ position: "relative", flex: "1 1 280px", minWidth: 0 }}>
              <Search
                size={18}
                style={{
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  insetInlineStart: "14px",
                  color: c.textMuted,
                  pointerEvents: "none",
                }}
              />
              <input
                type="search"
                className="pp-field"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={L("ابحث بالاسم أو القسم أو رقم الموظف...", "Search by name, department or employee ID...")}
                aria-label={L("بحث عن موظف", "Search employees")}
                style={{
                  ...controlStyle,
                  width: "100%",
                  padding: "0 14px",
                  paddingInlineStart: "42px",
                }}
              />
            </div>
            <div style={{ flex: "0 1 240px", minWidth: "180px" }}>
              <ReusableDropdown
                items={[
                  { id: "", label: L("كل الأقسام", "All departments") },
                  ...departmentOptions.map((dept) => ({ id: dept, label: dept })),
                ]}
                value={departmentFilter}
                onChange={(value) => setDepartmentFilter(String(value))}
                placeholder={L("كل الأقسام", "All departments")}
                ariaLabel={L("تصفية حسب القسم", "Filter by department")}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.label}
                searchable={departmentOptions.length > 7}
                theme={theme}
                language={language}
                inputStyle={{
                  minHeight: "46px",
                  borderColor: departmentFilter ? c.primary : c.border,
                  background: c.inputBackground,
                }}
              />
            </div>
            {isFiltering && (
              <button
                type="button"
                className="pp-btn pp-btn-secondary"
                onClick={clearFilters}
                style={{
                  ...controlStyle,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "0 14px",
                  background: c.surface,
                  color: c.textSecondary,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <X size={16} />
                {L("مسح", "Clear")}
              </button>
            )}
          </>
        )}
        <div
          style={{
            ...controlStyle,
            ...modalStyles.checkboxGroup,
            padding: "0 16px",
            background: allVisibleSelected ? c.primarySoft : c.surface,
            borderColor: allVisibleSelected ? c.primary : c.border,
            opacity: visiblePayrollData.length === 0 ? 0.5 : 1,
            marginInlineStart: "auto",
          }}
        >
          <input
            type="checkbox"
            id="selectAll"
            checked={allVisibleSelected}
            ref={(el) => {
              if (el) el.indeterminate = someVisibleSelected;
            }}
            onChange={handleSelectAllToggle}
            disabled={visiblePayrollData.length === 0}
            style={{
              ...checkboxStyle,
              cursor: visiblePayrollData.length === 0 ? "not-allowed" : "pointer",
            }}
          />
          <label
            htmlFor="selectAll"
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: c.textPrimary,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              whiteSpace: "nowrap",
            }}
          >
            {isFiltering ? L("تحديد النتائج", "Select results") : t("selectAll")}
            <span
              style={{
                background: c.primary,
                color: "#fff",
                borderRadius: "999px",
                padding: "1px 9px",
                fontSize: "12px",
                fontWeight: 700,
              }}
            >
              {visiblePayrollData.length}
            </span>
          </label>
        </div>
      </div>

      {isFiltering && (
        <div style={{ fontSize: "13px", color: c.textMuted, marginBottom: "12px" }}>
          {L(
            `${visiblePayrollData.length} من ${scopedPayrollData.length} موظف`,
            `${visiblePayrollData.length} of ${scopedPayrollData.length} employees`,
          )}
        </div>
      )}

      {/* ── Notices ───────────────────────────────────────────────────── */}
      {(paidCount > 0 || hiddenByCompanyFilterCount > 0) && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
          {paidCount > 0 &&
            notice(
              "warning",
              <AlertTriangle size={17} style={{ flexShrink: 0, marginTop: "1px", color: c.warning }} />,
              L(
                <>
                  <strong style={{ color: c.textPrimary }}>{paidCount}</strong> موظف تم صرف راتبهم بالفعل ومخفيين من القائمة — لا يمكن إعادة معالجة رواتب مدفوعة.
                </>,
                <>
                  <strong style={{ color: c.textPrimary }}>{paidCount}</strong> employee{paidCount === 1 ? "" : "s"}{" "}
                  already marked as paid {paidCount === 1 ? "is" : "are"} hidden from this list — paid payrolls can't be reprocessed.
                </>,
              ),
            )}
          {hiddenByCompanyFilterCount > 0 &&
            notice(
              "info",
              <Building2 size={17} style={{ flexShrink: 0, marginTop: "1px", color: c.info }} />,
              L(
                <>
                  <strong style={{ color: c.textPrimary }}>{hiddenByCompanyFilterCount}</strong> موظف من شركات أخرى مخفيين — يظهر فقط موظفو {selectedCompanyIds.length === 1 ? "الشركة المحددة" : "الشركات المحددة"}.
                </>,
                <>
                  <strong style={{ color: c.textPrimary }}>{hiddenByCompanyFilterCount}</strong> employee
                  {hiddenByCompanyFilterCount === 1 ? "" : "s"} from other companies{" "}
                  {hiddenByCompanyFilterCount === 1 ? "is" : "are"} hidden — only employees from the{" "}
                  {selectedCompanyIds.length === 1 ? "selected company" : "selected companies"} are shown here.
                </>,
              ),
            )}
        </div>
      )}

      {/* ── Employee list ─────────────────────────────────────────────── */}
      <div
        style={{
          border: `1px solid ${c.borderLight}`,
          borderRadius: "12px",
          overflow: "hidden",
          background: c.surface,
        }}
      >
        <div
          className="pp-scroll"
          style={{ ...modalStyles.employeeList, marginTop: "-1px" }}
        >
          {scopedPayrollData.length > 0 && visiblePayrollData.length === 0 && (
            <div
              style={{
                padding: "48px 24px",
                textAlign: "center",
                color: c.textMuted,
                fontSize: "14.5px",
              }}
            >
              {L("لا يوجد موظفين مطابقين للبحث.", "No employees match your search.")}{" "}
              <button
                type="button"
                onClick={clearFilters}
                style={{ background: "none", border: "none", color: c.primary, fontWeight: 700, cursor: "pointer", fontSize: "14.5px" }}
              >
                {L("مسح البحث", "Clear search")}
              </button>
            </div>
          )}

          {scopedPayrollData.length === 0 && (
            <div
              style={{
                padding: "48px 24px",
                textAlign: "center",
                color: c.textMuted,
                fontSize: "14.5px",
              }}
            >
              {rowsLoading
                ? L("جارٍ تحميل الموظفين…", "Loading employees…")
                : hasCompanyFilter
                  ? L("لا يوجد موظفين غير مدفوعين للشركة/الشركات المحددة.", "No unpaid employees found for the selected company/companies.")
                  : L("كل الموظفين في هذه الفترة تم صرف رواتبهم بالفعل.", "All employees for this period are already paid.")}
            </div>
          )}

          {isMultiCompany
            ? Object.entries(groupedByCompanyThenDepartment).map(([company, departmentsMap]) =>
                renderCompanyGroup(company, departmentsMap),
              )
            : Object.entries(groupedByDepartment).map(([department, employees]) =>
                renderDepartmentGroup(department, employees),
              )}
        </div>
      </div>

      {/* ── Selection notes (total lives in the modal footer) ─────────── */}
      {validSelectedEmployees.length > 0 && (hiddenSelectedCount > 0 || selectedByCompany) && (
        <div
          style={{
            marginTop: "16px",
            padding: "14px 16px",
            background: c.primarySoft,
            borderRadius: "10px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", color: c.textPrimary, fontSize: "14px", fontWeight: 600 }}>
            <CheckCircle size={17} style={{ color: c.primary, flexShrink: 0 }} />
            {L(
              `${validSelectedEmployees.length} موظف محدد`,
              `${validSelectedEmployees.length} employees selected`,
            )}
          </div>

          {hiddenSelectedCount > 0 && (
            <div style={{ fontSize: "13px", color: c.textSecondary }}>
              {L(
                `منهم ${hiddenSelectedCount} محدد ومخفي بسبب البحث — سيتم معالجتهم أيضاً.`,
                `${hiddenSelectedCount} of them are hidden by the search — they will still be processed.`,
              )}
            </div>
          )}

          {selectedByCompany && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {Object.entries(selectedByCompany).map(([company, { count, total }]) => (
                <span
                  key={company}
                  style={{
                    fontSize: "12.5px",
                    fontWeight: 600,
                    padding: "5px 10px",
                    borderRadius: "8px",
                    background: c.surface,
                    border: `1px solid ${c.borderLight}`,
                    color: c.textSecondary,
                  }}
                >
                  {company}: {count} • {money(total)}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StepChooseEmployees;
